import { normaliseWebsite } from '@crm/contracts';
import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import { describeError } from '../../lib/api.ts';
import { api, type CompanyView } from '../../lib/client.ts';
import { useMembers } from '../../lib/record-data.ts';
import { useSession } from '../../lib/session.tsx';
import { Button } from '../ui/button.tsx';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog.tsx';
import { Input } from '../ui/input.tsx';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select.tsx';

/** Create a company (TS-1750): name, website, industry and owner, the caller by default. */
export function CompanyDialog({
  onClose,
  onSaved,
}: {
  onClose: () => void;
  onSaved: (company: CompanyView) => void | Promise<void>;
}) {
  const session = useSession();
  const { members } = useMembers();
  const [name, setName] = useState('');
  const [website, setWebsite] = useState('');
  const [industry, setIndustry] = useState('');
  const [ownerId, setOwnerId] = useState(session.actor.userId);
  const [error, setError] = useState<string | null>(null);

  const values = {
    name: name.trim(),
    website: website.trim() || null,
    industry: industry.trim() || null,
    ownerId,
  };
  const badWebsite = !!values.website && !normaliseWebsite(values.website);
  const ready = values.name !== '' && !badWebsite;

  const save = useMutation({
    mutationFn: () => api.companies.create(values),
    onSuccess: onSaved,
    onError: (err) => setError(describeError(err)),
  });

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>New company</DialogTitle>
        </DialogHeader>
        <form
          id="company-form"
          className="flex flex-col gap-3"
          onSubmit={(e) => {
            e.preventDefault();
            if (ready) save.mutate();
          }}
        >
          <label className="flex flex-col gap-1 text-sm">
            Name
            <Input value={name} onChange={(e) => setName(e.target.value)} autoFocus />
          </label>
          <label className="flex flex-col gap-1 text-sm">
            Website
            <Input
              value={website}
              onChange={(e) => setWebsite(e.target.value)}
              placeholder="example.com"
              aria-invalid={badWebsite}
            />
          </label>
          {badWebsite && <WebsiteHint />}
          <label className="flex flex-col gap-1 text-sm">
            Industry
            <Input value={industry} onChange={(e) => setIndustry(e.target.value)} />
          </label>
          <label className="flex flex-col gap-1 text-sm">
            Owner
            <Select value={ownerId ?? ''} onValueChange={(v) => v && setOwnerId(v)}>
              <SelectTrigger aria-label="Company owner">
                <SelectValue placeholder="No owner" />
              </SelectTrigger>
              <SelectContent>
                {members
                  .filter((m) => m.status === 'active')
                  .map((m) => (
                    <SelectItem key={m.id} value={m.id}>
                      {m.name || m.email}
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
          </label>
          {error && (
            <p className="text-danger text-sm" role="alert">
              {error}
            </p>
          )}
        </form>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" form="company-form" disabled={!ready || save.isPending}>
            Create company
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function WebsiteHint() {
  return <span className="text-danger text-xs">Not a website, e.g. example.com</span>;
}
